import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Code,
  Database,
  Server,
  Webhook,
  CheckCircle2,
  Clock,
  AlertCircle,
  ExternalLink,
  Copy,
  Settings,
  Layers,
  Zap,
  Shield,
  FileText,
  ChevronRight,
  Globe,
  Building2,
  Wrench,
  BarChart3,
  RefreshCw,
  Check,
  Terminal,
  GitBranch,
  Box,
  Cpu,
  Lock,
  Key,
  MessageSquare,
  Cloud,
  HardDrive,
  Network,
  Workflow,
  FileCode,
  Boxes
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Code Block Component
const CodeBlock = ({ code, language = "bash", title }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="bg-slate-900 rounded-xl overflow-hidden">
      {title && (
        <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
          <span className="text-xs text-slate-400 font-mono">{title}</span>
          <span className="text-xs text-slate-500">{language}</span>
        </div>
      )}
      <div className="relative">
        <pre className="p-4 overflow-x-auto text-sm">
          <code className="text-emerald-400 font-mono">{code}</code>
        </pre>
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 p-2 text-slate-500 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
        >
          {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

// Tech Stack Card
const TechCard = ({ icon: Icon, name, version, description, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg transition-all group">
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <h4 className="font-semibold text-slate-900">{name}</h4>
        {version && <span className="text-xs text-slate-500">{version}</span>}
      </div>
    </div>
    <p className="text-sm text-slate-600">{description}</p>
  </div>
);

// API Endpoint Component
const APIEndpoint = ({ method, path, description, auth = true }) => {
  const methodColors = {
    GET: "bg-emerald-100 text-emerald-700",
    POST: "bg-blue-100 text-blue-700",
    PUT: "bg-amber-100 text-amber-700",
    DELETE: "bg-red-100 text-red-700",
    PATCH: "bg-violet-100 text-violet-700"
  };
  
  return (
    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
      <span className={`px-2 py-1 text-xs font-bold rounded ${methodColors[method]}`}>{method}</span>
      <code className="text-sm font-mono text-slate-700 flex-1">{path}</code>
      {auth && <Lock className="w-3 h-3 text-slate-400" title="Requires authentication" />}
      <span className="text-sm text-slate-500">{description}</span>
    </div>
  );
};

// Architecture Diagram Component
const ArchitectureDiagram = () => (
  <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-white">
    <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
      <Workflow className="w-5 h-5 text-cyan-400" />
      System Architecture
    </h3>
    <div className="grid grid-cols-4 gap-4 text-center text-sm">
      {/* Frontend Layer */}
      <div className="col-span-4 bg-blue-500/20 rounded-xl p-4 border border-blue-500/30">
        <p className="text-blue-400 font-semibold mb-2">Frontend Layer</p>
        <div className="flex justify-center gap-4">
          <div className="bg-slate-800 rounded-lg px-4 py-2">
            <p className="font-mono text-xs">React 18</p>
            <p className="text-slate-400 text-xs">SPA + PWA</p>
          </div>
          <div className="bg-slate-800 rounded-lg px-4 py-2">
            <p className="font-mono text-xs">Tailwind CSS</p>
            <p className="text-slate-400 text-xs">Utility-first</p>
          </div>
          <div className="bg-slate-800 rounded-lg px-4 py-2">
            <p className="font-mono text-xs">Shadcn/UI</p>
            <p className="text-slate-400 text-xs">Components</p>
          </div>
        </div>
      </div>
      
      {/* Arrow */}
      <div className="col-span-4 flex justify-center">
        <div className="w-0.5 h-8 bg-gradient-to-b from-blue-500 to-emerald-500" />
      </div>
      
      {/* API Layer */}
      <div className="col-span-4 bg-emerald-500/20 rounded-xl p-4 border border-emerald-500/30">
        <p className="text-emerald-400 font-semibold mb-2">API Layer</p>
        <div className="flex justify-center gap-4">
          <div className="bg-slate-800 rounded-lg px-4 py-2">
            <p className="font-mono text-xs">FastAPI</p>
            <p className="text-slate-400 text-xs">Python 3.11</p>
          </div>
          <div className="bg-slate-800 rounded-lg px-4 py-2">
            <p className="font-mono text-xs">Pydantic</p>
            <p className="text-slate-400 text-xs">Validation</p>
          </div>
          <div className="bg-slate-800 rounded-lg px-4 py-2">
            <p className="font-mono text-xs">JWT + RBAC</p>
            <p className="text-slate-400 text-xs">Auth</p>
          </div>
        </div>
      </div>
      
      {/* Arrow */}
      <div className="col-span-4 flex justify-center">
        <div className="w-0.5 h-8 bg-gradient-to-b from-emerald-500 to-violet-500" />
      </div>
      
      {/* Data Layer */}
      <div className="col-span-2 bg-violet-500/20 rounded-xl p-4 border border-violet-500/30">
        <p className="text-violet-400 font-semibold mb-2">Data Layer</p>
        <div className="bg-slate-800 rounded-lg px-4 py-2">
          <p className="font-mono text-xs">MongoDB Atlas</p>
          <p className="text-slate-400 text-xs">Document Store</p>
        </div>
      </div>
      
      {/* External Services */}
      <div className="col-span-2 bg-amber-500/20 rounded-xl p-4 border border-amber-500/30">
        <p className="text-amber-400 font-semibold mb-2">External Services</p>
        <div className="flex gap-2 justify-center">
          <div className="bg-slate-800 rounded-lg px-3 py-2">
            <p className="font-mono text-xs">Telegram</p>
          </div>
          <div className="bg-slate-800 rounded-lg px-3 py-2">
            <p className="font-mono text-xs">Claude AI</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ERP System Card
const ERPCard = ({ system, onConfigure }) => {
  const statusColors = {
    active: "bg-emerald-100 text-emerald-700 border-emerald-200",
    inactive: "bg-slate-100 text-slate-600 border-slate-200",
    error: "bg-red-100 text-red-700 border-red-200",
    pending_setup: "bg-amber-100 text-amber-700 border-amber-200",
    testing: "bg-blue-100 text-blue-700 border-blue-200"
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-all duration-300 group">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-slate-100 to-slate-50 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
            <Database className="w-6 h-6 text-slate-700" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{system.name}</h3>
            <p className="text-xs text-slate-500">{system.auth_type} authentication</p>
          </div>
        </div>
        {system.status && (
          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${statusColors[system.status] || statusColors.inactive}`}>
            {system.status.replace('_', ' ')}
          </span>
        )}
      </div>
      
      <p className="text-sm text-slate-600 mb-4">{system.description}</p>
      
      <div className="flex flex-wrap gap-2 mb-4">
        {system.features?.map((feature, idx) => (
          <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-lg">
            {feature}
          </span>
        ))}
      </div>
      
      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
        <span className={`text-xs font-medium ${
          system.setup_complexity === 'low' ? 'text-emerald-600' :
          system.setup_complexity === 'medium' ? 'text-amber-600' : 'text-red-600'
        }`}>
          {system.setup_complexity} complexity
        </span>
        <Button size="sm" variant="outline" onClick={() => onConfigure(system)} className="text-xs">
          Configure
        </Button>
      </div>
    </div>
  );
};

export default function DeveloperHubPage() {
  const [supportedSystems, setSupportedSystems] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchIntegrationData();
  }, []);

  const fetchIntegrationData = async () => {
    try {
      const token = localStorage.getItem("sterilemate_token");
      
      const systemsRes = await fetch(`${API}/integrations/supported-systems`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (systemsRes.ok) {
        const data = await systemsRes.json();
        setSupportedSystems(data.systems || []);
      }
      
      if (token) {
        const integrationsRes = await fetch(`${API}/integrations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (integrationsRes.ok) {
          const data = await integrationsRes.json();
          setIntegrations(data.integrations || []);
        }
      }
    } catch (error) {
      console.error("Failed to fetch integration data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigure = (system) => {
    toast.info(`Configuration for ${system.name} coming soon. Contact support for early access.`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center">
              <Code className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Developer Hub</h1>
              <p className="text-indigo-300">Technical Documentation & API Reference</p>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20">
              <p className="text-2xl font-bold">45+</p>
              <p className="text-sm text-slate-300">API Endpoints</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20">
              <p className="text-2xl font-bold">6</p>
              <p className="text-sm text-slate-300">ERP Integrations</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20">
              <p className="text-2xl font-bold">99.9%</p>
              <p className="text-sm text-slate-300">Uptime SLA</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20">
              <p className="text-2xl font-bold">&lt;100ms</p>
              <p className="text-sm text-slate-300">Avg Response</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: "overview", label: "Overview", icon: BarChart3 },
              { id: "architecture", label: "Architecture", icon: Boxes },
              { id: "api", label: "API Reference", icon: Code },
              { id: "integrations", label: "ERP Integrations", icon: Database },
              { id: "webhooks", label: "Webhooks", icon: Webhook },
              { id: "security", label: "Security", icon: Shield }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-indigo-600 text-indigo-600"
                    : "border-transparent text-slate-600 hover:text-slate-900"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-8">
            
            {/* Problem We Solve - Supply Chain Chaos */}
            <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-xl p-1">
              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">The Problem We Solve</h2>
                    <p className="text-red-600 font-semibold">Healthcare Supply Chain Chaos</p>
                  </div>
                </div>
                <p className="text-slate-600 mb-4">
                  Hospitals waste <span className="font-bold text-red-600">millions annually</span> on emergency sourcing, 
                  expedited shipping, and compliance failures. Staff spend hours calling vendors one-by-one 
                  while patients wait.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">$500K+</div>
                    <div className="text-sm text-slate-600">Lost per hospital/year</div>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <div className="text-2xl font-bold text-amber-600">3-5 Days</div>
                    <div className="text-sm text-slate-600">Traditional quote time</div>
                  </div>
                  <div className="text-center p-4 bg-emerald-50 rounded-lg">
                    <div className="text-2xl font-bold text-emerald-600">30 min</div>
                    <div className="text-sm text-slate-600">AskIsaac quote time</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Our Innovation - Telegram-First */}
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl p-1">
              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Our Innovation</h2>
                    <p className="text-cyan-600 font-semibold">Telegram-First UX</p>
                  </div>
                </div>
                <p className="text-slate-600 mb-4">
                  We meet healthcare workers <span className="font-bold text-cyan-600">where they are</span> — 
                  on the floor, not at desks. No app downloads. No complex interfaces. Just text from your phone 
                  and get back to patient care.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <span className="text-sm text-slate-700">No app download required</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <span className="text-sm text-slate-700">30-second request submission</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    <span className="text-sm text-slate-700">AI-powered intent routing</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tech Stack */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Layers className="w-5 h-5 text-indigo-600" />
                Technology Stack
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <TechCard 
                  icon={Globe}
                  name="React"
                  version="18.2"
                  description="Component-based SPA with hooks, context API, and react-router-dom"
                  color="bg-gradient-to-br from-cyan-500 to-blue-500"
                />
                <TechCard 
                  icon={Server}
                  name="FastAPI"
                  version="0.104"
                  description="High-performance Python web framework with automatic OpenAPI docs"
                  color="bg-gradient-to-br from-emerald-500 to-teal-500"
                />
                <TechCard 
                  icon={Database}
                  name="MongoDB"
                  version="Atlas"
                  description="Document database with flexible schemas and powerful aggregation"
                  color="bg-gradient-to-br from-green-500 to-emerald-500"
                />
                <TechCard 
                  icon={MessageSquare}
                  name="Telegram Bot"
                  version="API 6.9"
                  description="Real-time messaging for field workers via python-telegram-bot"
                  color="bg-gradient-to-br from-blue-500 to-indigo-500"
                />
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                <TechCard 
                  icon={Cpu}
                  name="Claude AI"
                  version="Sonnet"
                  description="LLM-powered intelligent routing and natural language processing"
                  color="bg-gradient-to-br from-amber-500 to-orange-500"
                />
                <TechCard 
                  icon={Lock}
                  name="JWT Auth"
                  version="RS256"
                  description="Token-based authentication with role-based access control (RBAC)"
                  color="bg-gradient-to-br from-red-500 to-rose-500"
                />
                <TechCard 
                  icon={FileCode}
                  name="Tailwind CSS"
                  version="3.4"
                  description="Utility-first CSS framework with custom design system"
                  color="bg-gradient-to-br from-violet-500 to-purple-500"
                />
                <TechCard 
                  icon={Box}
                  name="Shadcn/UI"
                  version="Latest"
                  description="Beautifully designed components built with Radix UI and Tailwind"
                  color="bg-gradient-to-br from-slate-600 to-slate-800"
                />
              </div>
            </div>

            {/* Key Features */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl p-6 text-white">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-cyan-300" />
                  Front-Line Concierge
                </h3>
                <p className="text-indigo-100 text-sm mb-4">
                  Mobile-first Telegram bot that meets healthcare workers where they are. 
                  Report issues, request supplies, track deliveries — all from the floor.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                    Natural language command parsing
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                    Photo/document attachments
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                    Real-time status notifications
                  </li>
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5 text-cyan-300" />
                  Back-Office Heavy Lifter
                </h3>
                <p className="text-emerald-100 text-sm mb-4">
                  Full ERP integration capability. GPS logs, photo evidence, and compliance 
                  data flows directly into existing hospital systems.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-300" />
                    Bi-directional ERP sync
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-300" />
                    Automated management fee calculations
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-300" />
                    HIPAA-compliant audit trails
                  </li>
                </ul>
              </div>
            </div>

            {/* Quick Start */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Terminal className="w-5 h-5 text-emerald-600" />
                Quick Start
              </h2>
              
              <CodeBlock 
                title="Authentication"
                language="bash"
                code={`# Get JWT token
curl -X POST ${API}/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email": "your@email.com", "password": "your_password"}'

# Use token in subsequent requests
curl -X GET ${API}/maintenance \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN"`}
              />
            </div>
          </div>
        )}

        {/* Architecture Tab */}
        {activeTab === "architecture" && (
          <div className="space-y-8">
            <ArchitectureDiagram />
            
            {/* Backend Architecture */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Server className="w-5 h-5 text-emerald-600" />
                Backend Architecture
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Core Stack</h3>
                  <CodeBlock 
                    title="requirements.txt (key dependencies)"
                    language="python"
                    code={`fastapi==0.104.1
uvicorn[standard]==0.24.0
motor==3.3.2          # Async MongoDB driver
pydantic==2.5.2       # Data validation
python-jose[cryptography]==3.3.0  # JWT handling
passlib[bcrypt]==1.7.4  # Password hashing
python-telegram-bot==20.7  # Telegram integration
anthropic==0.8.1      # Claude AI integration
httpx==0.25.2         # Async HTTP client`}
                  />
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Project Structure</h3>
                  <CodeBlock 
                    title="Backend Directory Layout"
                    language="text"
                    code={`/app/backend/
├── server.py           # Main FastAPI application & routes
├── requirements.txt    # Python dependencies
├── .env               # Environment variables (MONGO_URL, etc.)
├── models/            # Pydantic models & MongoDB schemas
├── routes/            # API route modules
├── services/          # Business logic services
├── integrations/      # ERP & external service adapters
└── tests/             # pytest test suites`}
                  />
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Database Design</h3>
                  <p className="text-slate-600 text-sm mb-3">
                    MongoDB document collections with flexible schemas supporting multi-tenant architecture:
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    {[
                      { name: "users", desc: "User accounts with RBAC roles", fields: "email, role, hospital_id" },
                      { name: "hospitals", desc: "Health system organizations", fields: "name, locations[], settings" },
                      { name: "maintenance", desc: "Work orders & service requests", fields: "status, photos[], gps_log" },
                      { name: "backorders", desc: "Supply chain requests", fields: "items[], quotes[], timeline" },
                      { name: "invoices", desc: "Contractor billing", fields: "line_items[], approvals[]" },
                      { name: "audit_logs", desc: "Compliance audit trail", fields: "action, user_id, timestamp" }
                    ].map((col, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-4">
                        <code className="text-indigo-600 font-mono text-sm">{col.name}</code>
                        <p className="text-slate-600 text-xs mt-1">{col.desc}</p>
                        <p className="text-slate-400 text-xs mt-2">{col.fields}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Frontend Architecture */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                Frontend Architecture
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">React Application</h3>
                  <CodeBlock 
                    title="Key Frontend Dependencies"
                    language="json"
                    code={`{
  "react": "^18.2.0",
  "react-router-dom": "^6.20.1",
  "tailwindcss": "^3.4.0",
  "@radix-ui/react-*": "Various",  // Headless UI primitives
  "lucide-react": "^0.294.0",      // Icons
  "sonner": "^1.2.4",              // Toast notifications
  "recharts": "^2.10.3",           // Charts
  "@react-pdf/renderer": "^3.1.14" // PDF generation
}`}
                  />
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Component Architecture</h3>
                  <CodeBlock 
                    title="Frontend Directory Layout"
                    language="text"
                    code={`/app/frontend/src/
├── App.js              # Router & global layout
├── components/
│   ├── ui/            # Shadcn/UI components (Button, Card, etc.)
│   └── [feature]/     # Feature-specific components
├── pages/             # Route-level page components
├── hooks/             # Custom React hooks
├── lib/               # Utilities & helpers
└── styles/            # Global CSS & Tailwind config`}
                  />
                </div>
              </div>
            </div>
            
            {/* Telegram Bot Architecture */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-500" />
                Telegram Bot Architecture
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Command Flow</h3>
                  <div className="space-y-2">
                    {[
                      { cmd: "/start", desc: "Initialize user session & link to hospital" },
                      { cmd: "/maintenance", desc: "Create new maintenance request" },
                      { cmd: "/status", desc: "Check status of active requests" },
                      { cmd: "/help", desc: "Show available commands" },
                      { cmd: "Natural text", desc: "AI-powered intent classification → route to handler" }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                        <code className="text-blue-600 font-mono text-sm">{item.cmd}</code>
                        <span className="text-sm text-slate-600">{item.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">AI Integration</h3>
                  <p className="text-slate-600 text-sm mb-3">
                    Claude Sonnet processes natural language messages to classify intent and extract entities:
                  </p>
                  <CodeBlock 
                    title="Intent Classification"
                    language="python"
                    code={`# User: "water filter needs replacing in OR 3"
{
  "intent": "maintenance_request",
  "category": "water_purification",
  "location": "OR 3",
  "urgency": "normal",
  "extracted_items": ["water filter"]
}`}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* API Reference Tab */}
        {activeTab === "api" && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Authentication</h2>
              <p className="text-slate-600 text-sm mb-4">
                All protected endpoints require a Bearer token in the Authorization header.
              </p>
              <div className="space-y-2">
                <APIEndpoint method="POST" path="/api/auth/register" description="Create new account" auth={false} />
                <APIEndpoint method="POST" path="/api/auth/login" description="Get JWT token" auth={false} />
                <APIEndpoint method="GET" path="/api/auth/me" description="Get current user" />
                <APIEndpoint method="POST" path="/api/auth/refresh" description="Refresh token" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Maintenance</h2>
              <div className="space-y-2">
                <APIEndpoint method="GET" path="/api/maintenance" description="List maintenance requests" />
                <APIEndpoint method="POST" path="/api/maintenance" description="Create maintenance request" />
                <APIEndpoint method="GET" path="/api/maintenance/{id}" description="Get request details" />
                <APIEndpoint method="PUT" path="/api/maintenance/{id}" description="Update request" />
                <APIEndpoint method="POST" path="/api/maintenance/{id}/photos" description="Upload photos" />
                <APIEndpoint method="PUT" path="/api/maintenance/{id}/complete" description="Mark completed" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Backorders & Supply Chain</h2>
              <div className="space-y-2">
                <APIEndpoint method="GET" path="/api/backorders" description="List backorders" />
                <APIEndpoint method="POST" path="/api/backorders" description="Create backorder" />
                <APIEndpoint method="POST" path="/api/backorders/{id}/quote" description="Submit quote" />
                <APIEndpoint method="PUT" path="/api/backorders/{id}/approve" description="Approve quote" />
                <APIEndpoint method="GET" path="/api/suppliers" description="List suppliers" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Invoicing</h2>
              <div className="space-y-2">
                <APIEndpoint method="GET" path="/api/invoices" description="List invoices" />
                <APIEndpoint method="POST" path="/api/invoices" description="Create invoice" />
                <APIEndpoint method="PUT" path="/api/invoices/{id}/approve" description="Approve invoice" />
                <APIEndpoint method="GET" path="/api/invoices/{id}/pdf" description="Download PDF" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">ERP Integrations</h2>
              <div className="space-y-2">
                <APIEndpoint method="GET" path="/api/integrations" description="List integrations" />
                <APIEndpoint method="POST" path="/api/integrations" description="Create integration" />
                <APIEndpoint method="POST" path="/api/integrations/{id}/test" description="Test connection" />
                <APIEndpoint method="POST" path="/api/integrations/{id}/sync/backorder" description="Push to ERP" />
                <APIEndpoint method="GET" path="/api/integrations/supported-systems" description="List supported ERPs" />
              </div>
            </div>
          </div>
        )}

        {/* Integrations Tab */}
        {activeTab === "integrations" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Supported ERP Systems</h2>
                <p className="text-slate-600 text-sm">Connect AskIsaac to your existing hospital infrastructure</p>
              </div>
              <Button variant="outline" onClick={fetchIntegrationData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
            
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
                    <div className="h-12 w-12 bg-slate-200 rounded-xl mb-4" />
                    <div className="h-5 bg-slate-200 rounded w-2/3 mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-full" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {supportedSystems.map((system) => (
                  <ERPCard key={system.id} system={system} onConfigure={handleConfigure} />
                ))}
              </div>
            )}
            
            {/* Integration Flow */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Integration Flow</h3>
              <div className="grid md:grid-cols-4 gap-4">
                {[
                  { step: "1", title: "Configure", desc: "Set up ERP credentials & mapping" },
                  { step: "2", title: "Test", desc: "Validate connection & permissions" },
                  { step: "3", title: "Map Fields", desc: "Configure data field mappings" },
                  { step: "4", title: "Go Live", desc: "Enable bi-directional sync" }
                ].map((item, idx) => (
                  <div key={idx} className="text-center">
                    <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold mx-auto mb-3">
                      {item.step}
                    </div>
                    <h4 className="font-semibold text-slate-900">{item.title}</h4>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Webhooks Tab */}
        {activeTab === "webhooks" && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Available Webhook Events</h2>
              <div className="space-y-3">
                {[
                  { event: "backorder.created", description: "New backorder request submitted" },
                  { event: "backorder.quoted", description: "Quote submitted for backorder" },
                  { event: "backorder.approved", description: "Backorder quote approved" },
                  { event: "maintenance.created", description: "New maintenance request" },
                  { event: "maintenance.assigned", description: "Work order assigned to contractor" },
                  { event: "maintenance.completed", description: "Work order completed" },
                  { event: "invoice.submitted", description: "Invoice submitted for approval" },
                  { event: "invoice.approved", description: "Invoice approved for payment" },
                  { event: "courier.pickup", description: "Courier pickup confirmed" },
                  { event: "courier.delivered", description: "Delivery completed with POD" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <code className="text-sm font-mono text-indigo-600">{item.event}</code>
                    <span className="text-sm text-slate-600">{item.description}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Webhook Payload Example</h3>
              <CodeBlock 
                title="maintenance.completed"
                language="json"
                code={`{
  "event": "maintenance.completed",
  "timestamp": "2026-01-15T14:30:00Z",
  "data": {
    "id": "maint_abc123",
    "title": "Water Filter Replacement",
    "location": "OR 3",
    "completed_by": "contractor@example.com",
    "photos": ["https://..."],
    "gps_log": {
      "checkin": { "lat": 29.7604, "lng": -95.3698 },
      "checkout": { "lat": 29.7604, "lng": -95.3698 }
    }
  },
  "signature": "sha256=abc123..."
}`}
              />
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-800 mb-2 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Webhook Security
              </h3>
              <p className="text-sm text-amber-700 mb-4">
                All webhook payloads include an HMAC-SHA256 signature. Verify before processing:
              </p>
              <CodeBlock 
                title="Signature Verification (Python)"
                language="python"
                code={`import hmac
import hashlib

def verify_webhook(payload: bytes, signature: str, secret: str) -> bool:
    expected = hmac.new(
        secret.encode(),
        payload,
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(f"sha256={expected}", signature)`}
              />
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === "security" && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-600" />
                Security Architecture
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                    <h4 className="font-semibold text-emerald-800 mb-2">Authentication</h4>
                    <ul className="text-sm text-emerald-700 space-y-1">
                      <li>• JWT tokens with RS256 signing</li>
                      <li>• Token expiration: 24 hours (configurable)</li>
                      <li>• Refresh token rotation</li>
                      <li>• bcrypt password hashing (cost factor 12)</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <h4 className="font-semibold text-blue-800 mb-2">Authorization (RBAC)</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• <code>super_admin</code> - Full platform access</li>
                      <li>• <code>hub_admin</code> - Hospital-level admin</li>
                      <li>• <code>staff</code> - Standard hospital user</li>
                      <li>• <code>contractor</code> - Service provider</li>
                      <li>• <code>supplier</code> - Product vendor</li>
                    </ul>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="p-4 bg-violet-50 rounded-xl border border-violet-100">
                    <h4 className="font-semibold text-violet-800 mb-2">HIPAA Compliance</h4>
                    <ul className="text-sm text-violet-700 space-y-1">
                      <li>• Zero-knowledge architecture for PHI</li>
                      <li>• WORM (Write Once Read Many) audit logs</li>
                      <li>• Automatic PHI detection & redaction</li>
                      <li>• Data encryption at rest (AES-256)</li>
                      <li>• TLS 1.3 for data in transit</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                    <h4 className="font-semibold text-amber-800 mb-2">API Security</h4>
                    <ul className="text-sm text-amber-700 space-y-1">
                      <li>• Rate limiting (100 req/min default)</li>
                      <li>• CORS whitelist configuration</li>
                      <li>• Input validation via Pydantic</li>
                      <li>• SQL injection protection (NoSQL)</li>
                      <li>• HMAC webhook signatures</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Environment Variables</h3>
              <p className="text-slate-600 text-sm mb-4">
                Required environment variables for secure deployment:
              </p>
              <CodeBlock 
                title=".env Configuration"
                language="bash"
                code={`# Database
MONGO_URL=mongodb+srv://...
DB_NAME=sterilemate

# Authentication
JWT_SECRET=your-256-bit-secret
JWT_ALGORITHM=HS256

# External Services
TELEGRAM_BOT_TOKEN=your-bot-token
ANTHROPIC_API_KEY=your-claude-key

# Optional: ERP Integration
ORACLE_CLIENT_ID=...
SAP_API_KEY=...`}
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div className="bg-slate-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Integrate?</h2>
          <p className="text-slate-400 mb-6 max-w-2xl mx-auto">
            Our team will work with your IT department to ensure a smooth integration 
            with your existing hospital infrastructure.
          </p>
          <div className="flex justify-center gap-4">
            <a href="mailto:developers@sterilemate.com">
              <Button className="bg-indigo-600 hover:bg-indigo-700">
                Contact Developer Support
              </Button>
            </a>
            <a href="tel:8009883892">
              <Button variant="outline" className="text-white border-white hover:bg-white/10">
                Call: 800.988.3892
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
